import { getObjectByPath, hasMatchUrlFormatAndKey } from '../utils';
import { pathToTreeKeys } from '../deleteMembers';
import { TYPES } from '../../flattenTree/utils/consts';
import { ActionsDispatcher } from 'simple-action-dispatcher';
import {
  findInMessagesByUrlFormat,
  findInOperationsByUrlFormat,
  findInRelatedGuidesByUrlFormat,
  findInPropertiesByUrlFormat,
  findInEventsByUrlFormat,
  findInClientEventsByUrlFormat,
} from '../../treeVisitors/helpers';
import { toUrlFormat } from '../../treeVisitors';
import { updateMemberPathInConfig } from '../moveMembersToCategories';
import { updateTree } from '../../updateTree';

export const replaceMemberPath = (member, oldValue, newValue) => {
  let found = false;
  member.path = member.path
    .split('.')
    .map((pathPart) => {
      if (pathPart === oldValue && !found) {
        found = true;
        return newValue;
      }
      return pathPart;
    })
    .join('.');
};

const replaceMemberLastKey = (category, { path, name, type }, lastKey) => {
  const member = category.members.find(
    (m) => m.path === path && m.type === type,
  );
  if (member) {
    const regex = new RegExp(`${lastKey}$`);
    member.path = member.path.replace(regex, toUrlFormat(name));
  }
};

const changeCategoryPath = (category, newPath) => {
  category.path = newPath;
  category.members.forEach((member) => {
    updateMemberPathInConfig(newPath, member);
  });
};

const changeSectionPath = (category, oldPath, newPath, memberPath) => {
  category.members.forEach((member) => {
    if (member.path === memberPath && member.type !== TYPES.SECTION) {
      return;
    }
    replaceMemberPath(member, oldPath, newPath);
  });
};

const shouldUpdateTree = (oldUrlFormat, newUrlFormat, editMode) =>
  oldUrlFormat !== '$w' && oldUrlFormat !== newUrlFormat && !editMode;

const defaultHandler = (objToChange, key, member, editMode, category) => {
  const msg = findInMessagesByUrlFormat(objToChange, key);
  if (msg) {
    msg.displayName = member.name;
    if (!editMode) {
      msg.urlFormat = toUrlFormat(member.name);
      replaceMemberLastKey(category, member, key);
    }
  }
};

const changeEventName = (objToChange, key, member, editMode, category) => {
  const event = findInEventsByUrlFormat(objToChange, key);
  if (event) {
    event.displayName = member.name;
    if (!editMode) {
      event.urlFormat = toUrlFormat(member.name);
      replaceMemberLastKey(category, member, key);
    }
  }
};

const changeCategoryName = (
  objectToChange,
  lastKey,
  member,
  editMode,
  category,
  docsTree,
) => {
  if (hasMatchUrlFormatAndKey(objectToChange, lastKey)) {
    objectToChange.data.displayName = member.name;
    const oldUrlFormat = objectToChange.data.urlFormat;
    const newUrlFormat = toUrlFormat(member.name);
    if (shouldUpdateTree(oldUrlFormat, newUrlFormat, editMode)) {
      updateTree(docsTree, member.path, newUrlFormat);
      changeCategoryPath(member, newUrlFormat);
    }
  }
};

const changeSectionName = (
  objectToChange,
  lastKey,
  member,
  editMode,
  category,
  docsTree,
) => {
  if (hasMatchUrlFormatAndKey(objectToChange, lastKey)) {
    objectToChange.data.displayName = member.name;
    const oldUrlFormat = objectToChange.data.urlFormat;
    const newUrlFormat = toUrlFormat(member.name);
    if (shouldUpdateTree(oldUrlFormat, newUrlFormat, editMode)) {
      updateTree(docsTree, member.path, newUrlFormat);
      changeSectionPath(category, oldUrlFormat, newUrlFormat, member.path);
    }
  }
};

const changeOperationName = (objToChange, key, member, editMode, category) => {
  const operation = findInOperationsByUrlFormat(objToChange, key);
  if (operation) {
    operation.displayName = member.name;
    if (!editMode) {
      operation.urlFormat = toUrlFormat(member.name);
      replaceMemberLastKey(category, member, key);
    }
  }
};

const changeClientEventName = (
  objToChange,
  key,
  member,
  editMode,
  category,
) => {
  const clientEvent = findInClientEventsByUrlFormat(objToChange, key);
  if (clientEvent) {
    clientEvent.displayName = member.name;
    if (!editMode) {
      clientEvent.urlFormat = member.urlFormat;
      replaceMemberLastKey(category, member, key);
    }
  }
};

const changeGuideName = (objToChange, key, member, editMode, category) => {
  const guide = findInRelatedGuidesByUrlFormat(objToChange, key);
  if (guide) {
    guide.displayName = member.name;
    if (!editMode) {
      guide.urlFormat = toUrlFormat(member.name);
      replaceMemberLastKey(category, member, key);
    }
  }
};

const changePropertyName = (objToChange, key, member, editMode, category) => {
  const property = findInPropertiesByUrlFormat(objToChange, key);
  if (property) {
    property.displayName = member.name;
    if (!editMode) {
      property.urlFormat = toUrlFormat(member.name);
      replaceMemberLastKey(category, member, key);
    }
  }
};

export const changeMembersNameEffect = (
  docsTree,
  member,
  editMode,
  category,
) => {
  const keys = getKeys(member);
  const lastKey = member.path.split('.').pop();
  const objectToChange = getObjectByPath(docsTree, keys);
  if (!objectToChange) {
    console.error(`ERROR::There in no object at the path ${member.path}.`);
    return;
  }
  const dispatcher = new ActionsDispatcher(defaultHandler);
  dispatcher
    .register(TYPES.CATEGORY, changeCategoryName)
    .register(TYPES.SECTION, changeSectionName)
    .register(TYPES.TAG, TYPES.SECTION)
    .register(TYPES.OPERATION, changeOperationName)
    .register(TYPES.MD, changeGuideName)
    .register(TYPES.PROPERTY, changePropertyName)
    .register(TYPES.CLIENT_EVENT, changeClientEventName)
    .register(TYPES.WEBHOOK, changeEventName)
    .register(TYPES.EVENT, TYPES.WEBHOOK);
  dispatcher.dispatch(
    member.type,
    objectToChange,
    lastKey,
    member,
    editMode,
    category,
    docsTree,
  );
};

const getKeys = (member) => {
  const { path, type } = member;
  if (shouldRemoveLastKey(type)) {
    return pathToTreeKeys(path).slice(0, -1);
  } else {
    return pathToTreeKeys(path);
  }
};

const shouldRemoveLastKey = (type) => {
  const shouldNotRemoveForTypes = {
    [TYPES.CATEGORY]: true,
    [TYPES.SECTION]: true,
    [TYPES.TAG]: true,
  };
  return !shouldNotRemoveForTypes[type];
};
